import React, { useState } from "react";
import { Box, IconButton, LinearProgress, Typography } from "@mui/material";
import { IoIosClose } from "react-icons/io";
import DragAndDrop from "../ReusableForm/DragAndDrop/DragAndDrop";  // Adjust the import path as necessary
import { useLanguage } from '../../context/LanguageContext/LanguageContext';

const FileUpload = ({
    onFileSelect,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    supportedFileTypes,
    uploadedFiles,
    setUploadedFiles,
    uploadIcon,
    buttonLabel = "Browse",
    fileInputRef,
}) => {
    const [dragActive, setDragActive] = useState(false);
    const { translations } = useLanguage();
    const t = translations;

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);

        // Filter out unsupported files
        const unsupported = files.filter(file => !supportedFileTypes.includes(file.type));

        // Prevent adding duplicate files by comparing both name and size
        const newFiles = files.filter(file =>
            !uploadedFiles.some(uploadedFile =>
                uploadedFile.name === file.name && uploadedFile.size === file.size
            )
        );

        if (unsupported.length > 0) {
            // Optionally show an alert or message for unsupported files
            // console.log("Some files are unsupported.");
        }

        if (newFiles.length > 0) {
            // Convert each file to Base64 and store it alongside the name and type
            const filesWithBase64 = newFiles.map((file) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                return new Promise((resolve) => {
                    reader.onloadend = () => {
                        const base64String = reader.result.split(",")[1]; 
                        resolve({
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            base64: base64String,
                        });
                    };
                });
            });

            Promise.all(filesWithBase64).then((newFilesData) => {
                setUploadedFiles((prev) => [...prev, ...newFilesData]);
            });
        }

        // Reset file input to allow the same file to be selected again (if needed)
        fileInputRef.current.value = null;

        // Call the onFileSelect callback if provided
        onFileSelect && onFileSelect(e);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const removeFile = (index) => {
        setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleButtonClick = (e) => {
        e.preventDefault();

        fileInputRef.current.click();
    };

    return (
        <Box>
            {/* Drag and Drop Section */}
            <Box sx={{ mt: 3 }}>
                <DragAndDrop
                    onDrop={onDrop}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onFileSelect={handleFileSelect}
                    supportedFileTypes={supportedFileTypes}
                    uploadIcon={uploadIcon}
                    buttonLabel={buttonLabel}
                    fileInputRef={fileInputRef}
                    handleButtonClick={handleButtonClick} 
                    uploadedFiles={uploadedFiles}
                    t={t}
                />
            </Box>

            {/* File List Section */}
            {uploadedFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ mb: 2, color: "#676767", fontWeight: 700 }}>
                        {t.uploading}- {uploadedFiles.length}/{uploadedFiles.length} files
                    </Typography>

                    <ul>
                        <Box>
                            {uploadedFiles.map((file, index) => {
                                const isUnsupported = !supportedFileTypes.includes(file.type);
                                return (
                                    <Box sx={{ mb: 2 }} key={index}>
                                        <Box
                                            sx={{
                                                border: `1px solid ${isUnsupported ? "#E41D1D" : "#E0E0E0"}`,
                                                borderRadius: "4px",
                                                padding: "8px",
                                                color: "#0F0F0F"
                                            }}
                                        >
                                            <li
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    listStyle: "none",
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600, color: "#0F0F0F" }}
                                                >
                                                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                                </Typography>

                                                {/* Close Icon for uploaded files */}
                                                <IconButton
                                                    onClick={() => removeFile(index)}
                                                    size="small"
                                                    sx={{
                                                        marginLeft: 2,
                                                        color: "#fff",
                                                        backgroundColor: "#E6E6E6",
                                                    }}
                                                >
                                                    <IoIosClose />
                                                </IconButton>
                                            </li>

                                            {/* Show LinearProgress for supported files only */}
                                            {!isUnsupported && (
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={file.progress || 0}
                                                    sx={{
                                                        height: 6,
                                                        backgroundColor: "#12538B",
                                                        "& .MuiLinearProgress-bar": {
                                                            backgroundColor: "#12538B",
                                                        },
                                                        marginTop: "8px",
                                                    }}
                                                />
                                            )}
                                        </Box>

                                        {/* Show error message under the border if the file is unsupported */}
                                        {isUnsupported && (
                                            <Typography sx={{ fontWeight: 500, fontSize: "10px", color: "#E41D1D" }}>
                                                {t.notSupported}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </ul>
                </Box>
            )}
        </Box>
    );
};

export default FileUpload;
